import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { BillingCard } from "../components/BillingCard";
import { BrandingCard } from "../components/BrandingCard";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import {
  notificationsSupported, playPing, showDesktopNotification,
} from "../lib/notifications";
import { UserAvatar, invalidateAvatar } from "../components/UserAvatar";
import { MONTHS_IT } from "../components/UserCardDialog";

// Crop quadrato centrale + resize 512px lato client (canvas) prima dell'upload
const squareCropResize = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    try {
      const side = Math.min(img.width, img.height);
      const sx = (img.width - side) / 2;
      const sy = (img.height - side) / 2;
      const out = 512;
      const canvas = document.createElement("canvas");
      canvas.width = out;
      canvas.height = out;
      canvas.getContext("2d").drawImage(img, sx, sy, side, side, 0, 0, out, out);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("crop"))), "image/jpeg", 0.9);
    } catch (e) {
      URL.revokeObjectURL(url);
      reject(e);
    }
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("img")); };
  img.src = url;
});

const Stat = ({ label, value, testId }) => (
  <div className="bg-surface border border-border rounded-xl shadow-sm p-6">
    <div className="text-[11px] uppercase tracking-widest text-text-secondary mb-2">{label}</div>
    <div data-testid={testId} className="text-3xl tracking-tight font-medium text-text-primary">{value}</div>
  </div>
);

export default function Profile() {
  const { user, setUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [prefSaving, setPrefSaving] = useState(false);
  const [permission, setPermission] = useState(
    notificationsSupported() ? Notification.permission : "unsupported"
  );

  // ─── I miei dati (telefono, compleanno, avatar — la mansione la assegna l'admin) ───
  const fileRef = useRef(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [savingData, setSavingData] = useState(false);
  const [phone, setPhone] = useState(user?.phone || "");
  const [city, setCity] = useState(user?.city || "");
  const [bDay, setBDay] = useState(user?.birthday_day ? String(user.birthday_day) : "");
  const [bMonth, setBMonth] = useState(user?.birthday_month ? String(user.birthday_month) : "");
  const [bYear, setBYear] = useState(user?.birthday_year ? String(user.birthday_year) : "");

  const onPickAvatar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.(jpe?g|png)$/i.test(file.name)) {
      toast.error("Formato non supportato: usa JPG o PNG.");
      return;
    }
    setAvatarBusy(true);
    try {
      const blob = await squareCropResize(file);
      const form = new FormData();
      form.append("file", blob, "avatar.jpg");
      const { data } = await api.post("/me/avatar", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      invalidateAvatar(user.id);
      setUser(data);
      toast.success("Foto profilo aggiornata.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Upload non riuscito. Riprova con un'altra immagine.");
    } finally {
      setAvatarBusy(false);
    }
  };

  const onRemoveAvatar = async () => {
    setAvatarBusy(true);
    try {
      const { data } = await api.delete("/me/avatar");
      invalidateAvatar(user.id);
      setUser(data);
      toast.success("Foto profilo rimossa.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Errore rimozione.");
    } finally {
      setAvatarBusy(false);
    }
  };

  const saveMyData = async (e) => {
    e.preventDefault();
    if ((bDay && !bMonth) || (!bDay && bMonth)) {
      toast.error("Compleanno: giorno e mese vanno indicati insieme.");
      return;
    }
    if (bYear && !bDay) {
      toast.error("Compleanno: indica anche giorno e mese.");
      return;
    }
    setSavingData(true);
    try {
      const { data } = await api.patch("/me/profile", {
        phone: phone.trim() || null,
        city: city.trim() || null,
        birthday_day: bDay ? Number(bDay) : null,
        birthday_month: bMonth ? Number(bMonth) : null,
        birthday_year: bYear ? Number(bYear) : null,
      });
      setUser(data);
      toast.success("Dati aggiornati.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Errore salvataggio.");
    } finally {
      setSavingData(false);
    }
  };

  const updatePref = async (patch) => {
    setPrefSaving(true);
    try {
      const { data } = await api.patch("/me/notification-prefs", patch);
      setUser(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Impossibile salvare la preferenza.");
    } finally {
      setPrefSaving(false);
    }
  };

  const onToggleDesktop = async (checked) => {
    if (checked && permission === "default") {
      const res = await Notification.requestPermission();
      setPermission(res);
      if (res !== "granted") return;
    }
    await updatePref({ desktop_notifications: checked });
  };

  const sendTestNotification = () => {
    showDesktopNotification({
      title: "Wattline",
      body: "Le notifiche desktop funzionano correttamente.",
      tag: "wl-notif-test",
    });
    if (user?.notification_sound) playPing();
    toast.success("Notifica di prova inviata.");
  };

  useEffect(() => {
    api.get("/me/stats").then(({ data }) => setStats(data));
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSuccess("");
    const errs = {};
    if (!current) errs.current = "Inserisci la password attuale.";
    if (next.length < 8) errs.next = "La nuova password deve avere almeno 8 caratteri.";
    if (next && confirm && next !== confirm) errs.confirm = "Le password non coincidono.";
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      setSuccess("Password aggiornata con successo.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <h1 className="text-3xl tracking-tight font-medium text-text-primary">Profilo</h1>
        <p className="text-sm text-text-secondary mt-1">Le tue informazioni e statistiche di utilizzo.</p>
      </div>

      <Card className="bg-surface border border-border rounded-xl shadow-sm p-8" data-testid="profile-info-card">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-text-secondary mb-1.5">Nome</div>
            <div className="text-sm text-text-primary">{user?.full_name || "—"}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-text-secondary mb-1.5">Email</div>
            <div className="text-sm text-text-primary">{user?.email}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-text-secondary mb-1.5">Ruolo</div>
            <div className="text-sm text-text-primary capitalize">{user?.role === "admin" ? "Amministratore" : "Operatore"}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-text-secondary mb-1.5">Mansione</div>
            <div className="text-sm text-text-primary" data-testid="profile-job-title-value">{user?.job_title || "—"}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-text-secondary mb-1.5">Ultimo accesso</div>
            <div className="text-sm text-text-primary">{user?.last_login ? new Date(user.last_login).toLocaleString("it-IT") : "—"}</div>
          </div>
        </div>
      </Card>

      <BillingCard />

      {user?.role === "admin" && <BrandingCard />}

      <Card className="bg-surface border border-border rounded-xl shadow-sm p-8" data-testid="profile-mydata-card">
        <h2 className="text-lg font-medium text-text-primary mb-1">I miei dati</h2>
        <p className="text-sm text-text-secondary mb-6">
          Foto e contatti visibili ai colleghi della tua organizzazione. La mansione la assegna l&apos;amministratore.
        </p>

        <div className="flex items-center gap-5 mb-7">
          <UserAvatar
            userId={user?.id}
            name={user?.full_name || user?.email}
            version={user?.avatar_version}
            size={72}
            testId="profile-avatar"
          />
          <div>
            <input
              ref={fileRef}
              type="file"
              accept=".jpg,.jpeg,.png"
              className="hidden"
              onChange={onPickAvatar}
              data-testid="avatar-file-input"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={avatarBusy}
                data-testid="avatar-upload-btn"
                className="wl-btn wl-btn--ghost wl-btn--sm"
              >
                {avatarBusy ? "Attendere…" : "Carica foto"}
              </button>
              {user?.avatar_version && (
                <button
                  type="button"
                  onClick={onRemoveAvatar}
                  disabled={avatarBusy}
                  data-testid="avatar-remove-btn"
                  className="wl-btn wl-btn--ghost wl-btn--sm"
                >
                  Rimuovi
                </button>
              )}
            </div>
            <div className="text-xs text-text-muted mt-1.5">JPG o PNG, max 2 MB. Ritagliata quadrata in automatico.</div>
          </div>
        </div>

        <form onSubmit={saveMyData} className="space-y-4 max-w-md" data-testid="profile-mydata-form">
          <div className="wl-field">
            <label htmlFor="profile-phone" className="wl-label">Telefono</label>
            <input
              id="profile-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              maxLength={30}
              placeholder="es. +39 333 1234567"
              data-testid="profile-phone-input"
              className="wl-input"
            />
          </div>
          <div className="wl-field">
            <label htmlFor="profile-city" className="wl-label">Città</label>
            <input
              id="profile-city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              maxLength={80}
              placeholder="es. Sesto Fiorentino"
              data-testid="profile-city-input"
              className="wl-input"
            />
            <span className="wl-helper">Facoltativa: usata per il meteo in dashboard. Se vuota vale la città dell&apos;azienda.</span>
          </div>
          <div className="wl-field">
            <span className="wl-label">Compleanno</span>
            <div className="flex gap-2">
              <select
                value={bDay}
                onChange={(e) => setBDay(e.target.value)}
                data-testid="profile-birthday-day"
                className="wl-input flex-1"
              >
                <option value="">Giorno</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <select
                value={bMonth}
                onChange={(e) => setBMonth(e.target.value)}
                data-testid="profile-birthday-month"
                className="wl-input flex-1"
              >
                <option value="">Mese</option>
                {MONTHS_IT.slice(1).map((m, i) => (
                  <option key={m} value={i + 1}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>
                ))}
              </select>
              <input
                type="number"
                min={1900}
                max={new Date().getFullYear()}
                value={bYear}
                onChange={(e) => setBYear(e.target.value)}
                placeholder="Anno (opz.)"
                data-testid="profile-birthday-year"
                className="wl-input flex-1"
              />
            </div>
            <span className="wl-helper">Giorno e mese insieme; l&apos;anno è facoltativo.</span>
          </div>
          <button
            type="submit"
            disabled={savingData}
            data-testid="profile-mydata-save"
            className="wl-btn wl-btn--primary"
          >
            {savingData ? "Salvataggio…" : "Salva dati"}
          </button>
        </form>
      </Card>

      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <Stat label="Richieste totali" value={stats.total_requests} testId="stat-total" />
          <Stat label="Questo mese" value={stats.this_month} testId="stat-month" />
          <Stat label="Cache hit rate" value={`${stats.cache_hit_rate}%`} testId="stat-cache-rate" />
        </div>
      )}

      <Card className="bg-surface border border-border rounded-xl shadow-sm p-8" data-testid="profile-notifications-card">
        <h2 className="text-lg font-medium text-text-primary mb-1">Notifiche</h2>
        <p className="text-sm text-text-secondary mb-6">
          Le notifiche desktop arrivano solo quando questa scheda non è in primo piano; quando è attiva vedi già i badge nella barra laterale.
        </p>

        <div className="space-y-5 max-w-md">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-text-primary">Notifiche desktop</div>
              <div className="text-xs text-text-secondary mt-0.5">Nuovi messaggi interni e aggiornamenti sui ticket di supporto.</div>
            </div>
            <Switch
              checked={!!user?.desktop_notifications}
              onCheckedChange={onToggleDesktop}
              disabled={prefSaving || permission === "unsupported"}
              data-testid="profile-desktop-notifications-toggle"
            />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-text-primary">Suono</div>
              <div className="text-xs text-text-secondary mt-0.5">Un ping discreto insieme alla notifica.</div>
            </div>
            <Switch
              checked={!!user?.notification_sound}
              onCheckedChange={(checked) => updatePref({ notification_sound: checked })}
              disabled={prefSaving}
              data-testid="profile-notification-sound-toggle"
            />
          </div>

          {permission === "unsupported" && (
            <div data-testid="profile-notifications-unsupported" className="text-xs text-text-secondary bg-page border border-border px-3 py-2 rounded-lg">
              Il tuo browser non supporta le notifiche desktop.
            </div>
          )}
          {permission === "denied" && (
            <div data-testid="profile-notifications-denied" className="text-sm text-danger bg-danger-subtle border border-danger-subtle px-3 py-2 rounded-lg">
              Il browser sta bloccando le notifiche per questo sito. Sbloccale dalle impostazioni del sito (icona del lucchetto nella barra degli indirizzi) e riprova.
            </div>
          )}

          <button
            type="button"
            onClick={sendTestNotification}
            disabled={permission !== "granted"}
            data-testid="profile-test-notification-btn"
            className="wl-btn wl-btn--ghost wl-btn--sm"
          >
            Invia notifica di prova
          </button>
        </div>
      </Card>

      <Card className="bg-surface border border-border rounded-xl shadow-sm p-8">
        <h2 className="text-lg font-medium text-text-primary mb-1">Cambia password</h2>
        <p className="text-sm text-text-secondary mb-6">Per motivi di sicurezza ti chiediamo la password attuale.</p>

        <form onSubmit={onSubmit} className="space-y-4 max-w-md" data-testid="profile-password-form">
          <div className="wl-field">
            <label htmlFor="profile-current-password" className="wl-label wl-label--required">Password attuale</label>
            <input
              id="profile-current-password"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              autoComplete="current-password"
              data-testid="profile-current-password"
              className={`wl-input ${errors.current ? "is-error" : ""}`}
              aria-invalid={!!errors.current}
            />
            {errors.current && <span className="wl-error-msg">{errors.current}</span>}
          </div>
          <div className="wl-field">
            <label htmlFor="profile-new-password" className="wl-label wl-label--required">Nuova password</label>
            <input
              id="profile-new-password"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              autoComplete="new-password"
              data-testid="profile-new-password"
              className={`wl-input ${errors.next ? "is-error" : ""}`}
              aria-invalid={!!errors.next}
            />
            {errors.next
              ? <span className="wl-error-msg">{errors.next}</span>
              : <span className="wl-helper">Almeno 8 caratteri. Meglio se includi maiuscole, numeri e simboli.</span>}
          </div>
          <div className="wl-field">
            <label htmlFor="profile-confirm-password" className="wl-label wl-label--required">Conferma nuova password</label>
            <input
              id="profile-confirm-password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              autoComplete="new-password"
              data-testid="profile-confirm-password"
              className={`wl-input ${errors.confirm ? "is-error" : ""}`}
              aria-invalid={!!errors.confirm}
            />
            {errors.confirm && <span className="wl-error-msg">{errors.confirm}</span>}
          </div>
          {error && <div data-testid="profile-error" className="text-sm text-danger bg-danger-subtle border border-danger-subtle px-3 py-2 rounded-lg">{error}</div>}
          {success && <div data-testid="profile-success" className="text-sm text-success-strong bg-success-subtle border border-success-subtle px-3 py-2 rounded-lg">{success}</div>}
          <button
            type="submit"
            disabled={submitting}
            data-testid="profile-password-submit"
            className="wl-btn wl-btn--primary"
          >
            {submitting ? "Salvataggio…" : "Aggiorna password"}
          </button>
        </form>
      </Card>
    </div>
  );
}
